import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { HelperService } from '../../../shared/services/helper.service';
import { UsersService } from '../users.service';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from '../../../shared/services/toast.service';
import { environment } from '../../../../environments/environment';

interface DropDown {
  label: string;
  value: string;
}

interface User {
  user_id: string;
  name: string;
  email?: string;
  gender?: DropDown;
  avatar?: string | null;
  designation?: DropDown;
  manager_id: string;
  is_admin?: boolean;
  is_affiliate?: boolean;
  status?: boolean;
  groups: any[];
}

@Component({
  selector: 'app-admin-users-model',
  templateUrl: './admin-users-model.component.html',
  styleUrls: ['./admin-users-model.component.css'],
})
export class AdminUsersModelComponent implements OnInit {
  @Input() isUserModalVisible: boolean = false;
  @Input() userData: any = {};
  @Input() isEdit: boolean = false;

  @Output() closeUserModal = new EventEmitter<boolean>();
  @Output() updateState = new EventEmitter<any>();

  user: any;
  users: User[] = [];
  visible: boolean = false;
  designations: any = [];
  gender: DropDown[] = [
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
  ];
  managerList: User[] = [];
  selectedGroups: any[] = [];
  searchText: string = '';
  isLoading: boolean = false;
  groupListData: any[] = [];

  helper = inject(HelperService);
  usersService = inject(UsersService);
  cookieService = inject(CookieService);
  toastService = inject(ToastService);
  userPayload: { start: string; search: any; include_user: any };
  is_iel: boolean;

  constructor(private fb: FormBuilder) {}
  formGroup!: FormGroup;
  initializeForm(): void {
    this.formGroup = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      designation: [null, Validators.required],
      gender: ['', Validators.required],
      manager_id: [''],
      is_admin: [false],
      is_affiliate: [false],
      is_iel: [false],
      groups: [[]],
    });
  }

  onGroupSearch(event: any) {
    this.fetchGroups(event?.filter);
  }

  onSubmit(): void {
    let { groups, ...formData } = this.formGroup.value;
    if (!this.isEdit) {
      this.usersService
        .addUsers({
          ...formData,
          is_admin: formData.is_admin ? 'yes' : 'no',
          is_affiliate: formData.is_affiliate ? 'yes' : 'no',
          is_iel: formData.is_iel ? 'yes' : 'no',
          designation: typeof formData.designation === 'object'
            ? formData.designation.value
            : formData.designation,
        })
        .subscribe(
          (data: any) => {
            try {
              const parsedData = data;
              if (parsedData.error) {
                this.toastService.error(parsedData.message);
              } else {
                this.toastService.success(parsedData.message);
                this.visible = false;
                if (groups?.length) {
                  this.addGroupData();
                }
              }
              this.closeUserModal.emit(false);
            } catch (error) {
              console.error('Error parsing decoded data:', error);
            }
          },
          (error) => {
            console.error('Error fetching users', error);
          }
        );
    } else {
      this.usersService
        .updateUsers({
          ...formData,
          is_admin: formData.is_admin ? 'yes' : 'no',
          is_affiliate: formData.is_affiliate ? 'yes' : 'no',
          is_iel: formData.is_iel ? 'yes' : 'no',
          user_id: this.userData?.user_id,
          designation: typeof formData.designation === 'object'
            ? formData.designation.value
            : formData.designation,
        })
        .subscribe(
          (data: any) => {
            try {
              const parsedData = data;
              if (parsedData.error) {
                this.toastService.error(parsedData.message);
              } else {
                this.toastService.success(parsedData?.message);
                this.visible = false;
                if (groups?.length) {
                  this.addGroupData();
                }
                this.userList();
                this.closeUserModal.emit(false);
              }
            } catch (error) {
              console.error('Error parsing decoded data:', error);
            }
          },
          (error) => {
            console.error('Error fetching users', error);
          }
        );
    }
  }

  onManagerSearch(event: any) {
    this.userList(event.filter);
  }
  onDesignationSearch(event: any) {
    this.designationList(event.query);
  }

  addGroupData() {
    if (this.formGroup.get('groups')?.value.length) {
      const ielData = {
        user_id: this.userData?.user_id,
        groups: this.formGroup
          .get('groups')
          ?.value.map((item: any) => item.group_id)
          .join(', '),
      };

      const ielPayload = { ...ielData };

      this.usersService.addIelGroup(ielPayload).subscribe(
        (data: any) => {
          try {
            const parsedData = data;
            if (parsedData.error) {
              // alert(parsedData.message)
              // this.toastService.error(parsedData.message)
            } else {
              // alert(parsedData.message)
              // this.toastService.success(parsedData.message)
              this.visible = false;
            }
          } catch (error) {
            console.error('Error parsing decoded data:', error);
          }
        },
        (error) => {
          console.error('Error fetching users', error);
        }
      );
    }
  }

  onIsIelChange(value: boolean): void {
    if (!value) this.formGroup.get('groups')?.setValue([]);
  }

  getGroupName(groupId: number) {
    const group = this.groupListData.find((g) => g.group_id === groupId);
    return group ? group.name : 'Unknown';
  }

  patchForm(): void {
    this.formGroup.patchValue({
      name: this.userData?.name || '',
      email: this.userData?.email || '',
      designation: this.userData?.designation || '',
      gender: this.userData?.gender || '',
      manager_id: this.userData?.manager_id ? this.userData.manager_id : '',
      is_admin: this.userData?.is_admin === 'yes',
      is_affiliate: this.userData?.is_affiliate === 'yes',
      is_iel: this.userData?.is_iel === 'yes',
      groups: this.extractGroups(this.userData?.iel),
    });
  }

  designationList(search?: any) {
    const designationPayload = { search };

    this.usersService.getDesignations(designationPayload).subscribe(
      (data: any) => {
        try {
          const parsedData = data;
          const des_array: any[] = [];
          for (const item of parsedData.data) {
            if (item.designation) {
              des_array.push({
                label: item?.designation,
                value: item?.designation,
              });
            }
          }
          this.designations = des_array || [];

          // Filter existing designations based on input
          let designations = this.designations.filter((designation: any) =>
            designation.value == search
          );

          // If the searched value is not found, add it as an option
          if (designations.length == 0 && search != undefined) {
            this.designations.unshift({
              label: search,
              value: search,
            });
          }
        } catch (error) {
          console.error('Error parsing decoded data:', error);
        }
      },
      (error) => {
        console.error('Error fetching users', error);
      }
    );
  }

  userIELList() {
    const ielPayload = { user_id: this.userData?.user_id };
    this.usersService.getIelGroup(ielPayload).subscribe(
      (data: any) => {
        try {
          const parsedData = data;
          const iel_array: any[] = [];
          // for (const item of parsedData.data) {
          //   const newData = this.groupListData.filter(
          //     (items: any) => items.group_id === item.group_id
          //   );
          //   iel_array.push(...newData);
          // }
          this.selectedGroups = iel_array || [];
          //this.selectedGroups=parsedData;

          if (this.selectedGroups.length > 0) {
            this.is_iel = true;
          }
          this.visible = true;
        } catch (error) {
          console.error('Error parsing decoded data:', error);
        }
      },
      (error) => {
        console.error('Error fetching users', error);
      }
    );
  }

  removeGroup(groupId: number) {
    const groupsControl = this.formGroup.get('groups');
    if (!groupsControl?.value) return;

    const updatedGroups = groupsControl.value.filter(
      (group: { group_id: number }) => group.group_id !== groupId
    );

    this.formGroup.patchValue({ groups: updatedGroups });
  }

  extractGroups(groups: any[]): any[] {
    return groups
      .filter((g) => g.group_id)
      .map((g) => ({
        group_id: g.group.group_id,
        name: g.group.name,
      }));
  }

  ngOnInit(): void {
    this.fetchGroups();
    this.initializeForm();
    if (this.isEdit) {
      this.userList(false, this.userData.manager_id);
      this.patchForm();
      this.userIELList();
    } else {
      this.userList();
    }
  }

  fetchGroups(search?: any) {
    const payload = { type: 'all', link: 'all', search };
    this.usersService.getAllGroups(payload).subscribe(
      (data: any) => {
        try {
          const parsedData = data;
          this.groupListData = parsedData.data || [];
        } catch (error) {
          console.error('Error parsing decoded data:', error);
        }
      },
      (error) => {
        console.log('Error fetching users', error);
      }
    );
  }

  userList(search?: any, memberId?: any) {
    this.isLoading = true;
    this.userPayload = {
      start: '0',
      search: this.searchText || search,
      include_user: memberId,
    };
    this.usersService.getUsers(this.userPayload).subscribe(
      (data: any) => {
        try {
          this.isLoading = false;

          const parsedData = data;
          this.users = parsedData.data || [];
          this.users = this.users.map((user: any) => ({
            ...user,
            status: user.status === 'active',
          }));

          this.managerList = parsedData.data || [];
        } catch (error) {
          this.isLoading = false;

          console.error('Error parsing decoded data:', error);
        }
      },
      (error) => {
        console.error('Error fetching users', error);
      }
    );
  }

  fetchDesignations(search: string = ''): void {
    this.usersService.getDesignations({ search }).subscribe(
      (data: any) => {
        this.designations =
          data?.data?.map((item: any) => ({
            label: item.designation || '',
            value: item.designation || '',
          })) || [];
      },
      (error) => console.error('Error fetching designations:', error)
    );
  }

  selectUser(selectedUser: any, userForm?: NgForm): void {
    if (userForm) userForm.reset();
    this.user = {
      ...selectedUser,
      is_admin: selectedUser.is_admin === 'yes',
      is_affiliate: selectedUser.is_affiliate === 'yes',
      gender: this.gender.find(
        (g) => g.value === selectedUser.gender?.value
      ) || { label: '', value: '' },
      designation: this.designations.find(
        (d: any) => d.value === selectedUser.designation?.value
      ) || { label: '', value: '' },
    };
    this.visible = true;
  }

  closeModal(): void {
    this.closeUserModal.emit(false);
    this.visible = false;
  }
}
